/**
 * Behaviour layer for the Loomi RECOMMENDATIONS widget's product tiles
 * (home / PDP / cart app blocks) — the Engagement-powered carousels whose
 * tiles render the ceres-card template inside <br-product-card> shadow
 * roots (scope: `.br-recommendations`; the autosuggest widget's tiles are
 * handled separately by assets/bloomreach-autosuggest.js).
 *
 * Engagement's catalogue lags the Data Hub exclusions and carries no
 * style/style_colourway attributes, so the raw tiles show Combined Listing
 * parents, colourway-suffixed titles, and empty swatch slots. Per tile:
 *
 * - the title is trimmed to the style name (segment after the LAST " - "
 *   removed, change-tracked so the widget's in-place re-renders re-trim);
 * - the product's type and availability are read from Shopify's own
 *   `/products/<handle>.js` endpoint (cached per path); Combined Listing
 *   parents are hidden — the authoritative signal, since Discovery's keyword
 *   recall for a family is not reliable enough to infer exclusion from (some
 *   responses carry url-less stub docs) — and products with no available
 *   variant are hidden too, since Engagement's recommendations are not
 *   inventory-aware;
 * - one Discovery search per STYLE resolves the family's colourway data
 *   (the same API and fields the PLP uses), shared across tiles;
 * - as a fallback, a tile whose product is absent from its own family's
 *   Discovery results is hidden — the excluded-parent / ghost case for
 *   items exclusion has removed while Engagement still recommends them;
 * - a tile missing the PLP's hover image (some Engagement engines do not
 *   return `alt_image`) gets one injected from the family's Discovery
 *   `alt_image`, falling back to the product JSON's second gallery image;
 * - visible tiles are capped at VISIBLE_LIMIT per widget, so every
 *   placement shows the same count regardless of how many exclusions the
 *   served set carried (the app blocks request more than the cap);
 * - the app's widget endpoint serves at most 10 items no matter what the
 *   block requests, so each widget is topped up once from the Engagement
 *   SDK (`exponea.getRecommendation`, which honours larger sizes): extra
 *   items are rendered as additional `<br-product-card>` elements re-using
 *   the widget's own template, and flow through this module's usual
 *   trim/exclude/swatch/cap pipeline like any served tile.
 * - an empty `.ceres-card__swatches` slot is filled with the PLP cluster:
 *   own colour first, one sibling, then +N (hex swatches only, mirroring
 *   the card template's markup so the widget's injected CSS styles it).
 *
 * Config (account id / domain key) is read from the autosuggest config
 * JSON, which snippets/search-modal.liquid renders on every page.
 */

const SEARCH_ENDPOINT = 'https://core.dxpapi.com/api/v1/core/';
const BR_UID_COOKIE = '_br_uid_2';
const TILE_SWATCH_LIMIT = 2;
const RECOMMENDATION_SCOPE = '.br-recommendations';
const VISIBLE_LIMIT = 12;

const COMBINED_LISTING_TYPE = 'Combined Listing';

/** @type {Map<string, Promise<{paths: Set<string>, entries: Map<string, {urls: string[], titles: string[], swatches: string[], altImage: string}>} | null>>} */
const familyCache = new Map();

/** @type {Map<string, Promise<{type: string, available: boolean, images: string[]} | null>>} Product JSON essentials per pathname. */
const productCache = new Map();

/** @type {WeakMap<Element, string>} Last text this module wrote per element. */
const appliedText = new WeakMap();

/** @type {WeakSet<ShadowRoot>} Shadow roots already being observed. */
const observedRoots = new WeakSet();

/**
 * Recommendation tiles never show the card template's variant-image
 * fallback: Engagement items carry no colourway arrays, so the template
 * falls back to the widget's own image swatches — the exact look the hex
 * clusters replaced on the PLP. A tile whose family has hex data gets the
 * proper cluster injected; one without shows no swatches, matching the
 * PLP's single-colour behaviour.
 * @type {CSSStyleSheet | null}
 */
const variantFallbackSheet = typeof CSSStyleSheet !== 'undefined' ? new CSSStyleSheet() : null;
variantFallbackSheet?.replaceSync('.ceres-card__variants { display: none; }');

/** @type {{accountId: string, domainKey: string} | null | undefined} */
let configCache;

/** @returns {{accountId: string, domainKey: string} | null} */
function readConfig() {
  if (configCache !== undefined) return configCache;
  try {
    const parsed = JSON.parse(document.getElementById('bloomreach-autosuggest-config')?.textContent ?? 'null');
    configCache = parsed?.accountId && parsed?.domainKey ? { accountId: parsed.accountId, domainKey: parsed.domainKey } : null;
  } catch {
    configCache = null;
  }
  return configCache;
}

/** @returns {string} Bloomreach visitor id from the pixel cookie, or a stub. */
function brUid() {
  const cookie = document.cookie.split('; ').find((entry) => entry.startsWith(`${BR_UID_COOKIE}=`));
  return cookie ? decodeURIComponent(cookie.slice(BR_UID_COOKIE.length + 1)) : 'uid=0000000000000:v=11.8:ts=0:hc=1';
}

/** @param {string} value @returns {string} The url's pathname. */
function pathOnly(value) {
  return value.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
}

/**
 * Strips the colourway segment after the LAST " - ", the same display rule
 * as the PLP card template. Change-tracked: re-runs only when the widget
 * has overwritten our value, so re-renders re-trim and nothing double-trims.
 * @param {Element} element - The title element.
 */
function trimColourway(element) {
  const current = element.textContent ?? '';
  if (appliedText.get(element) === current) return;
  const trimmed = current.trim();
  const suffixIndex = trimmed.lastIndexOf(' - ');
  if (suffixIndex > 0) element.textContent = trimmed.slice(0, suffixIndex);
  appliedText.set(element, element.textContent ?? '');
}

/**
 * Resolves the product's type, availability and gallery images from
 * Shopify's product JSON endpoint, cached per pathname. Resolves null when
 * the path is not a product page or the request fails, so callers only act
 * on positive data.
 * @param {string} path - The product pathname (`/products/<handle>`).
 * @returns {ReturnType<typeof productCache.get>}
 */
function lookupProduct(path) {
  let promise = productCache.get(path);
  if (promise) return promise;
  if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[\w-]+$/i.test(path)) return Promise.resolve(null);

  promise = fetch(`${path}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      if (typeof product?.type !== 'string') return null;
      const images = Array.isArray(product.images) ? product.images.filter((image) => typeof image === 'string') : [];
      return { type: product.type, available: product.available !== false, images };
    })
    .catch(() => null);
  productCache.set(path, promise);
  return promise;
}

/**
 * Resolves a style family's Discovery presence + colourway arrays, cached
 * per style name. Resolves null when the request fails, so callers can
 * tell "family unknown" apart from "product absent from its family".
 * @param {string} styleName - The trimmed style title.
 * @returns {ReturnType<typeof familyCache.get>}
 */
function lookupFamily(styleName) {
  let promise = familyCache.get(styleName);
  if (promise) return promise;

  const config = readConfig();
  if (!config) return Promise.resolve(null);

  const url = new URL(SEARCH_ENDPOINT);
  url.searchParams.set('account_id', config.accountId);
  url.searchParams.set('domain_key', config.domainKey);
  url.searchParams.set('request_type', 'search');
  url.searchParams.set('search_type', 'keyword');
  url.searchParams.set('q', styleName);
  url.searchParams.set('rows', '24');
  url.searchParams.set('start', '0');
  url.searchParams.set('fl', 'pid,url,alt_image,style_colourway_urls,style_colourway_titles,style_colourway_swatches');
  url.searchParams.set('request_id', String(Math.floor(1e12 + Math.random() * 9e12)));
  url.searchParams.set('_br_uid_2', brUid());
  url.searchParams.set('url', window.location.href);

  promise = fetch(url.toString())
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => {
      if (!payload?.response) return null;
      const paths = new Set();
      const entries = new Map();
      for (const doc of payload.response.docs ?? []) {
        if (typeof doc.url !== 'string') continue;
        const docPath = pathOnly(doc.url);
        paths.add(docPath);
        entries.set(docPath, {
          urls: doc.style_colourway_urls ?? [],
          titles: doc.style_colourway_titles ?? [],
          swatches: doc.style_colourway_swatches ?? [],
          altImage: typeof doc.alt_image === 'string' && doc.alt_image.startsWith('https://') ? doc.alt_image : '',
        });
      }
      return { paths, entries };
    })
    .catch(() => null);
  familyCache.set(styleName, promise);
  return promise;
}

/**
 * Builds the PLP swatch cluster into the tile's empty swatch slot: own
 * colour first, one sibling, then +N. Markup mirrors the card template so
 * the widget's injected stylesheet styles it.
 * @param {Element} slot - The tile's `.ceres-card__swatches` container.
 * @param {{urls: string[], titles: string[], swatches: string[]}} entry - The colourway arrays.
 * @param {string} path - The tile's product pathname.
 */
function renderSwatches(slot, entry, path) {
  const items = [];
  const seen = new Set();
  entry.urls.forEach((itemUrl, index) => {
    const swatch = entry.swatches[index];
    if (typeof itemUrl !== 'string' || typeof swatch !== 'string') return;
    if (!(itemUrl.startsWith('/') || itemUrl.startsWith('https://'))) return;
    if (!(swatch.startsWith('#') && (swatch.length === 4 || swatch.length === 7))) return;
    if (seen.has(itemUrl)) return;
    seen.add(itemUrl);
    const item = { url: itemUrl, title: entry.titles[index] ?? '', swatch };
    if (itemUrl.split('?')[0] === path) items.unshift(item);
    else items.push(item);
  });
  if (items.length === 0) return;

  const cluster = document.createElement('div');
  cluster.className = 'ceres-card__style-colourways';
  for (const item of items.slice(0, TILE_SWATCH_LIMIT)) {
    const link = document.createElement('a');
    link.className = 'ceres-card__style-colourway-link';
    link.href = item.url;
    link.setAttribute('aria-label', `View ${item.title || 'colourway'}`);
    const chip = document.createElement('span');
    chip.className = 'swatch';
    chip.style.setProperty('--swatch-background', item.swatch);
    chip.setAttribute('aria-hidden', 'true');
    link.append(chip);
    cluster.append(link);
  }
  const hidden = items.length - TILE_SWATCH_LIMIT;
  if (hidden > 0) {
    const count = document.createElement('span');
    count.className = 'hidden-swatches__count';
    count.textContent = `+${hidden}`;
    cluster.append(count);
  }
  slot.append(cluster);
}

/**
 * Injects the PLP hover image when the tile is missing one (some
 * Engagement engines do not return `alt_image` to the card template).
 * Prefers the family's Discovery `alt_image` — the exact image the PLP
 * card hovers to — falling back to the product's second gallery image.
 * @param {ShadowRoot} root - The tile's shadow root.
 * @param {{altImage: string} | undefined} entry - The tile's family entry.
 * @param {{images: string[]} | null} product - The tile's product JSON essentials.
 */
function renderHoverImage(root, entry, product) {
  const primary = root.querySelector('img.ceres-card__image-primary');
  if (!primary || root.querySelector('img.ceres-card__image-hover')) return;

  let source = entry?.altImage || product?.images?.[1] || '';
  if (source.startsWith('//')) source = `https:${source}`;
  if (!source.startsWith('https://')) return;

  const joiner = source.includes('?') ? '&' : '?';
  const hover = document.createElement('img');
  hover.className = 'ceres-card__image-hover';
  hover.src = `${source}${joiner}width=640`;
  hover.srcset = [400, 640, 900].map((width) => `${source}${joiner}width=${width} ${width}w`).join(', ');
  const sizes = primary.getAttribute('sizes');
  if (sizes) hover.setAttribute('sizes', sizes);
  hover.alt = '';
  hover.loading = 'lazy';
  primary.after(hover);
}

/**
 * Applies the widget-level visibility pass: excluded tiles stay hidden and
 * the rest are capped at VISIBLE_LIMIT per widget, in served order. Runs
 * after every per-tile state change, so a late exclusion frees a slot for
 * the next capped tile.
 */
function refreshVisibility() {
  for (const widget of document.querySelectorAll(RECOMMENDATION_SCOPE)) {
    let visible = 0;
    for (const card of widget.querySelectorAll('br-product-card')) {
      const host = /** @type {HTMLElement} */ (card);
      if (host.dataset.brExcluded === '1') {
        host.style.display = 'none';
        continue;
      }
      visible += 1;
      host.style.display = visible > VISIBLE_LIMIT ? 'none' : '';
    }
  }
}

/**
 * Marks a tile excluded (or clears the mark) and re-runs the visibility pass.
 * @param {HTMLElement} host - The `<br-product-card>` element.
 * @param {boolean} excluded - Whether the tile's product is excluded.
 */
function setExcluded(host, excluded) {
  const next = excluded ? '1' : '0';
  if (host.dataset.brExcluded === next) return;
  host.dataset.brExcluded = next;
  refreshVisibility();
}

/** @type {WeakSet<Element>} Widgets already topped up (or in flight). */
const toppedUp = new WeakSet();

/** How many recommendations to ask the Engagement SDK for per widget. */
const TOP_UP_POOL = 40;

/**
 * Tops a widget up beyond the app endpoint's 10-item ceiling: fetches the
 * same engine's recommendations through the Engagement SDK and appends the
 * items the widget was not served as additional `<br-product-card>`
 * elements, re-using the widget's own template and config. Appended cards
 * are processed by the standard pipeline via the mutation observer.
 * @param {Element} widget - The `.br-recommendations` container.
 */
function topUpWidget(widget) {
  if (toppedUp.has(widget)) return;

  const sourceCard = /** @type {HTMLElement & {product: object, htmlTemplate: string, injectedCss: string, customStyles: string, currencySymbol: string, displayVariants: unknown}} */ (
    widget.querySelector('br-product-card')
  );
  if (!sourceCard?.htmlTemplate) return;

  const widgetKey = widget.id.replace('br-recommendations-widget-', '');
  const engineId = window.bloomreachConnector?.widgets?.[widgetKey]?.engine_id;
  const getRecommendation = window.exponea?.getRecommendation?.bind(window.exponea);
  if (!engineId || typeof getRecommendation !== 'function') return;

  toppedUp.add(widget);
  getRecommendation({
    recommendationId: engineId,
    size: TOP_UP_POOL,
    fillWithRandom: true,
    callback: (items) => {
      if (!Array.isArray(items)) return;

      const grid = sourceCard.parentElement;
      if (!grid) return;

      const shown = new Set();
      for (const card of widget.querySelectorAll('br-product-card')) {
        const href = card.shadowRoot?.querySelector('a[href]')?.getAttribute('href');
        if (href) shown.add(pathOnly(href));
      }

      for (const item of items) {
        if (typeof item?.handle !== 'string' || !item.handle) continue;
        if (item.productType === COMBINED_LISTING_TYPE) continue;
        const path = `/products/${item.handle}`;
        if (shown.has(path)) continue;
        shown.add(path);

        const extra = /** @type {typeof sourceCard} */ (document.createElement('br-product-card'));
        // SDK items carry minVariantPrice/maxVariantPrice where the widget's
        // endpoint items carry price/sale_price — the card component only
        // reads the latter, so an unmapped item renders no price at all.
        // The lowest variant price stands in as the single displayed price;
        // min-vs-max says nothing about discounts, so no strike-through.
        extra.product = { ...item, url: path, price: item.price ?? item.minVariantPrice };
        extra.htmlTemplate = sourceCard.htmlTemplate;
        extra.injectedCss = sourceCard.injectedCss;
        extra.customStyles = sourceCard.customStyles;
        extra.currencySymbol = sourceCard.currencySymbol;
        extra.displayVariants = sourceCard.displayVariants;
        grid.append(extra);
      }
      refreshVisibility();
    },
  });
}

/** @type {MutationObserver} */
const observer = new MutationObserver(() => process());

/** Processes every recommendations tile currently in the document. */
function process() {
  for (const widget of document.querySelectorAll(RECOMMENDATION_SCOPE)) {
    if (widget.querySelector('br-product-card')) topUpWidget(widget);
  }
  for (const card of document.querySelectorAll(`${RECOMMENDATION_SCOPE} br-product-card`)) {
    const root = card.shadowRoot;
    if (!root) continue;

    if (!observedRoots.has(root)) {
      observedRoots.add(root);
      observer.observe(root, { childList: true, subtree: true, characterData: true });
      if (variantFallbackSheet && !root.adoptedStyleSheets.includes(variantFallbackSheet)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, variantFallbackSheet];
      }
    }

    const host = /** @type {HTMLElement} */ (card);
    const link = root.querySelector('a[href]');
    const href = link?.getAttribute('href') ?? '';
    if (!link || href === '#') {
      setExcluded(host, true);
      continue;
    }

    const title = root.querySelector('.ceres-card__title');
    if (title) trimColourway(title);

    const path = pathOnly(href);
    const styleName = title?.textContent?.trim() ?? '';
    if (!styleName) continue;

    void lookupProduct(path).then((product) => {
      if (product?.type === COMBINED_LISTING_TYPE) {
        setExcluded(host, true);
        return undefined;
      }
      if (product?.available === false) {
        setExcluded(host, true);
        return undefined;
      }

      return lookupFamily(styleName).then((family) => {
        const entry = family?.entries.get(path);
        renderHoverImage(root, entry, product);
        if (!family) return;

        // Fallback exclusion signal: Discovery answered for this family but
        // the recommended product is not in it — an excluded or unpublished
        // item Engagement is still serving.
        if (family.paths.size > 0 && !family.paths.has(path)) {
          setExcluded(host, true);
          return;
        }
        setExcluded(host, false);

        const slot = root.querySelector('.ceres-card__swatches');
        if (slot && !slot.querySelector('.ceres-card__style-colourway-link')) {
          if (entry) renderSwatches(slot, entry, path);
        }
      });
    });
  }
  refreshVisibility();
}

observer.observe(document.body, { childList: true, subtree: true });
process();
